/**
 * Crypto primitives for device-token wrapping. Two paths:
 *
 *  - PIN: AES-GCM key derived from PBKDF2(pin, salt, 600_000 iters, SHA-256).
 *    Salt is server-issued and rate-limited (10/hr per device) so offline
 *    brute-force is impossible.
 *
 *  - WebAuthn PRF: AES-GCM key derived from the authenticator's
 *    HMAC-secret extension output. Brute-force requires bypassing the
 *    OS biometric/passcode — out of scope.
 *
 * Both paths land in the same shape: an AES-GCM key + a randomly-chosen
 * IV used to wrap the JWT bytes.
 */

const PBKDF2_ITERATIONS = 600_000;
const KEY_LENGTH_BITS = 256;
const IV_LENGTH = 12; // AES-GCM standard

const enc = new TextEncoder();
const dec = new TextDecoder();

// TS 5.7+ narrowed Uint8Array's buffer type so `Uint8Array` defaults to
// `Uint8Array<ArrayBufferLike>` which isn't assignable to BufferSource
// (which expects `Uint8Array<ArrayBuffer>`). Runtime is identical; this
// helper just does the safe widening once.
function bs(b: Uint8Array): BufferSource {
  return b as unknown as BufferSource;
}

/** Generate a fresh random IV. Must be unique per wrap. */
export function newIv(): Uint8Array {
  return crypto.getRandomValues(new Uint8Array(IV_LENGTH));
}

/** Generate a fresh random salt. Used only when WebAuthn PRF is unavailable. */
export function newSalt(): Uint8Array {
  return crypto.getRandomValues(new Uint8Array(16));
}

/**
 * Derive a wrapping key from a PIN. `salt` MUST come from the server
 * (rate-limited via /auth/device/unlock-salt) so attackers can't run
 * offline brute force.
 */
export async function deriveKeyFromPin(pin: string, salt: Uint8Array): Promise<CryptoKey> {
  const baseKey = await crypto.subtle.importKey(
    'raw',
    bs(enc.encode(pin)),
    { name: 'PBKDF2' },
    false,
    ['deriveKey'],
  );
  return crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: bs(salt),
      iterations: PBKDF2_ITERATIONS,
      hash: 'SHA-256',
    },
    baseKey,
    { name: 'AES-GCM', length: KEY_LENGTH_BITS },
    false, // not extractable — never leaves SubtleCrypto
    ['encrypt', 'decrypt'],
  );
}

/**
 * Derive a wrapping key from a WebAuthn PRF extension result. The PRF
 * output is HMAC-secret-derived per credential — same authenticator,
 * same input → same key. Different authenticator (or revoked credential)
 * → impossible to recover the key.
 */
export async function deriveKeyFromPrf(prfOutput: ArrayBuffer): Promise<CryptoKey> {
  return crypto.subtle.importKey(
    'raw',
    prfOutput,
    { name: 'AES-GCM', length: KEY_LENGTH_BITS },
    false,
    ['encrypt', 'decrypt'],
  );
}

/** Wrap a JWT (UTF-8 string) with AES-GCM. Returns ciphertext bytes. */
export async function wrapToken(key: CryptoKey, iv: Uint8Array, token: string): Promise<Uint8Array> {
  const plaintext = enc.encode(token);
  const ct = await crypto.subtle.encrypt({ name: 'AES-GCM', iv: bs(iv) }, key, bs(plaintext));
  return new Uint8Array(ct);
}

/** Unwrap a previously-wrapped token. Throws on auth-tag mismatch (wrong key). */
export async function unwrapToken(key: CryptoKey, iv: Uint8Array, ciphertext: Uint8Array): Promise<string> {
  const pt = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: bs(iv) }, key, bs(ciphertext));
  return dec.decode(pt);
}

// ── Base64url helpers (WebAuthn payloads are base64url-encoded) ───────

export function base64urlEncode(bytes: Uint8Array): string {
  let bin = '';
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export function base64urlDecode(s: string): Uint8Array {
  const pad = s.length % 4 === 0 ? '' : '='.repeat(4 - (s.length % 4));
  const b64 = (s + pad).replace(/-/g, '+').replace(/_/g, '/');
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}
